import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';

type ConfirmActionModalProps = {
  open: boolean;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  destructive?: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

const ConfirmActionModal = ({
  open,
  title = 'Are you sure you want to remove',
  message,
  confirmText = 'Remove',
  cancelText = 'Cancel',
  isLoading = false,
  destructive = true,
  onClose,
  onConfirm,
}: ConfirmActionModalProps): JSX.Element => {
  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          px: 3,
          pt: 3,
          pb: 3,
          position: 'relative',
          overflow: 'visible',
        },
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={isLoading}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: '#364152',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent
        sx={{
          p: 0,
          textAlign: 'center',
        }}
      >
        <Typography
          component="h2"
          sx={{
            mt: 4,
            mb: 2,
            fontSize: 'var(--font-size-125-rem, 20px)',
            lineHeight: 1.25,
            fontWeight: 700,
            color: 'var(--color-text-primary, rgba(13, 32, 53, 0.87))',
            fontFamily: 'var(--fontFamily2, Poppins)',
          }}
        >
          {message}
        </Typography>

        {/*         <Typography
          sx={{
            mb: 4,
            fontSize: '16px',
            lineHeight: 1.5,
            fontWeight: 400,
            color: '#5B6475',
          }}
        >
          This action can't be undone.
        </Typography> */}

        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            variant="contained"
            fullWidth
            sx={{
              mb: 2,
              height: 42,
              borderRadius: '999px',
              textTransform: 'none',

              color: 'var(--error-contrastText, #FFF)',
              fontFamily: 'var(--fontFamily2, Poppins)',
              fontSize: 'var(--font-size-09375-rem, 15px)',
              fontStyle: 'normal',
              fontWeight: 'var(--fontWeightMedium, 500)',
              lineHeight: '26px',
              letterSpacing: '0.46px',

              fontFeatureSettings: '"liga" 0, "clig" 0',

              boxShadow: 'none',
              backgroundColor: destructive ? 'var(--error-main, #DC2626)' : 'var(--primary-main, #1976d2)',

              '&:hover': {
                boxShadow: 'none',
                backgroundColor: destructive ? 'var(--error-dark, #C81E1E)' : 'var(--primary-dark, #1565c0)',
              },
            }}
          >
            {confirmText}
          </Button>

          <Button
            onClick={onClose}
            disabled={isLoading}
            variant="text"
            sx={{
              textTransform: 'none',

              color: 'var(--primary-main, var(--primary-color))',
              fontFamily: 'var(--fontFamily2, Poppins)',
              fontSize: 'var(--font-size-09375-rem, 15px)',
              fontStyle: 'normal',
              fontWeight: 'var(--fontWeightMedium, 500)',
              lineHeight: '26px',
              letterSpacing: '0.46px',
              fontFeatureSettings: '"liga" 0, "clig" 0',

              minHeight: 42,
              padding: 0,
            }}
          >
            {cancelText}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmActionModal;
